import { Component, OnInit, Input, EventEmitter, Output, signal, computed, OnChanges, TemplateRef, ViewChild, ElementRef, NO_ERRORS_SCHEMA } from '@angular/core';
import { FlowObjectDefinition, FlowObjectType } from '../../../../models/flow-object.model';
import { FlowDefinition } from '../../../../models/flow.model';
import { ToastrService } from 'ngx-toastr';
import { Enums } from 'src/app/shared/enums';
import { Utils } from '../../../../shared/utils';
import { IBaseOption } from '../../../../models/base.model';
import { ConfigSchema, ConfigSchemaSectionAccessLevel, ConfigSchemaSectionDocumentClass, ConfigSchemaSectionExtraContent, ExtraContentAttachment, ExtraContentType } from '../../../../models/config-schema.model';
import { DocumentLegalValueCombination, DocumentLegalValueCombinationDescription } from '../../../../models/edocs.model';
import { IMPORTS } from './_imports';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'fod-extra-content',
    templateUrl: './fod-extra-content.component.html',
    styleUrl: './fod-extra-content.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})

export class FlowObjectDetailsExtraContentComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('configSchemaRef') configSchemaRef: TemplateRef<HTMLElement>;
    @ViewChild('configSchemaEditorRef') configSchemaEditorRef: ElementRef;
    // #endregion

    // #region [Type props]
    ExtraContentType = ExtraContentType;
    Utils = Utils;
    // #endregion

    // #region [const]
    MAX_ATTACHMENTS: number = 3 as const;
    // #endregion

    // #region [props]
    configSchemaSection = new ConfigSchemaSectionExtraContent();
    documentLegalValueCombinationOptions: IBaseOption[] = [];
    configSchemaDialog: MatDialogRef<HTMLElement, any>;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectDefinition);
    $flowDefinition = signal(null as FlowDefinition);
    $isReadOnlyMode = signal(false);
    $tinyMceOptionsMessage = signal(null as string);
    $tinyMceOptionsWaitingMessage = signal(null as string);
    $attachments = signal([] as ExtraContentAttachment[]);
    $waitingMessage = signal('');
    $contentType = signal(null as ExtraContentType);
    $documentClass = signal(null as ConfigSchemaSectionDocumentClass);
    $accessLevel = signal(null as ConfigSchemaSectionAccessLevel);
    $message = signal('');
    $configSchemaFlat = signal(null as string);
    $forgotWaitingMessage = signal(false);
    $enableExtraContent = signal(false);
    // #endregion

    // #region [computeds]
    $canAddAttachment = computed(() => this.$attachments().length < this.MAX_ATTACHMENTS);
    $disableFields = computed(() => this.$isReadOnlyMode()
        || (
            this.$shouldShowEnableExtraContent()
            && !this.$enableExtraContent()
        )
    );
    $shouldShowEnableExtraContent = computed(() => this.$model() != null && this.$model().typeId != FlowObjectType.FinishContent);
    $waitingMessageLength = computed(() => {
        let div = document.createElement('div');
        div.innerHTML = this.$waitingMessage()?.replaceAll('<br />', '\n');
        return (div.textContent || div.innerText || '').length;
    });
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectDefinition;
    @Input() _flowDefinition: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Input() _tinyMceOptionsMessage: string;
    @Input() _tinyMceOptionsWaitingMessage: string;
    @Output() _submitEvent = new EventEmitter<FlowObjectDefinition>();
    @Output() _closeEvent = new EventEmitter<any>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private toastr: ToastrService
    ) {
        for (let item in DocumentLegalValueCombination) {
            if (!Number.isNaN(Number.parseInt(item)) && Number.parseInt(item) < 7) {
                this.documentLegalValueCombinationOptions.push({
                    value: +item,
                    description: DocumentLegalValueCombinationDescription.get(+item)
                });
            }
        }
    }

    ngOnInit() {
        if (this.$model()?.configSchema != null) {
            this.initFromModel();
        }
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowDefinition.set(this._flowDefinition);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
        this.$tinyMceOptionsMessage.set(this._tinyMceOptionsMessage);
        this.$tinyMceOptionsWaitingMessage.set(this._tinyMceOptionsWaitingMessage);
    }

    addAttachment() {
        if (this.$isReadOnlyMode()) return;

        if (this.$canAddAttachment()) {
            Utils.updSignal(this.$attachments, x => {
                x.push(new ExtraContentAttachment());
                return x;
            });
        }
    }

    removeAttachment(item: ExtraContentAttachment) {
        if (this.$isReadOnlyMode()) return;

        this.$attachments.set(this.$attachments().filter(x => x.id != item.id));
    }

    getFinalDocumentNamePreview(label: string): string {
        return 'E-Flow | {0}{1} - {2} | {3} | {4} | {5}'
            .replace('{0}', this.$flowDefinition().patriarchInfo.split('-')[0].trim())
            .replace('{1}', this.$flowDefinition().patriarchInfo.split('-')[0].trim() != this.$flowDefinition().organizationInfo.split('-')[0].trim()
                ? ' - ' + this.$flowDefinition().organizationInfo.split('-')[0].trim()
                : ''
            )
            .replace('{2}', this.$flowDefinition().unitInfo.split('-')[0].trim())
            .replace('{3}', this.$flowDefinition().name.trim())
            .replace('{4}', this.$model().name.trim())
            .replace('{5}', label.trim());
    }

    onUpdateDocumentClass(event: ConfigSchemaSectionDocumentClass, item?: ExtraContentAttachment) {
        if (item != null) {
            item.documentClass = event;
        } else {
            Utils.setSignal(this.$documentClass, event);
        }
    }

    onUpdateAccessLevel(event: ConfigSchemaSectionAccessLevel, item?: ExtraContentAttachment) {
        if (item != null) {
            item.accessLevel = event;
        } else {
            Utils.setSignal(this.$accessLevel, event);
        }
    }

    onEnableExtraContentChange(event: boolean) {
        this.$enableExtraContent.set(event);

        if (!event) {
            this.$attachments.set([]);
        }
    }

    showConfigSchema() {
        let tempObject = {
            model: JSON.parse(JSON.stringify(this.$model())) as FlowObjectDefinition,
            configSchemaSection: JSON.parse(JSON.stringify(this.configSchemaSection))
        };
        delete tempObject.model.configSchema;

        this.$configSchemaFlat.set(btoa(encodeURIComponent(JSON.stringify(tempObject))));

        this.configSchemaDialog = this.dialog.open(this.configSchemaRef, {
            minWidth: '800px'
        });

        this.configSchemaDialog.afterOpened().subscribe(() => {
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.scrollLeft = 0;
                this.configSchemaEditorRef.nativeElement.scrollTop = 0;
            }, 1);
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.style.opacity = '1';
            }, 100);
        });

        this.configSchemaDialog.afterClosed().subscribe(async () => {
            if (this.$isReadOnlyMode()) return;

            try {
                let modalObject = JSON.parse(decodeURIComponent(atob(this.configSchemaEditorRef.nativeElement.value)));
                let modalObjectPrint = JSON.stringify(modalObject);
                delete modalObject.model.configSchema;

                // checa se o tipo da origem do conteúdo é o mesmo tipo do conteúdo atual e se a origem não é um FlowDefinition
                if (modalObject.model.typeId != this.$model().typeId || modalObject.model.targetId != null) {
                    this.toastr.error(Enums.Messages.InvalidConfigSchemaSourceType, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                // checa se o código-fonte foi alterado
                if (JSON.stringify(tempObject) != modalObjectPrint) {
                    this.configSchemaSection = JSON.parse(JSON.stringify(modalObject.configSchemaSection)) as ConfigSchemaSectionExtraContent;

                    // limpando deliberadamente as configurações de Classe enquanto o E-Docs não deixar os GUIDs
                    // de classe documental únicos dentre todos os ambientes, a exemplo dos do Organograma
                    this.configSchemaSection.documentClass = new ConfigSchemaSectionDocumentClass();
                    this.configSchemaSection.attachments.forEach(x => x.documentClass = new ConfigSchemaSectionDocumentClass());
                    Utils.updSignal(this.$attachments, x => {
                        x.forEach(xx => xx.documentClass = new ConfigSchemaSectionDocumentClass());
                        return x;
                    });

                    this.initFromModel(true);
                }
            } catch (error) {
                this.toastr.error(Enums.Messages.InvalidFlowObjectConfigSchemaSource, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }
        });
    }

    onSubmit() {
        if (this.$isReadOnlyMode()) return false;

        if (
            !this.$shouldShowEnableExtraContent()
            && (
                Utils.isNullOrEmpty(this.$waitingMessage())
                || this.$waitingMessage().length < 10
            )
        ) {
            this.toastr.warning(Enums.Messages.MandatoryWaitingMessage, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            this.$forgotWaitingMessage.set(true);
            return false;
        } else {
            this.$forgotWaitingMessage.set(false);
        }

        if (this.$contentType() == ExtraContentType.Attachment) {
            if (this.$enableExtraContent() == true && this.$attachments().length == 0) {
                this.toastr.warning(Enums.Messages.MandatoryAttachment, Enums.Messages.Warning, Utils.getToastrErrorOptions());
                return false;
            }

            if (this.$attachments().some(x => Utils.isNullOrEmpty(x.documentClass.id))) {
                this.toastr.warning(Enums.Messages.MandatoryAttachmentDocumentClass, Enums.Messages.Warning, Utils.getToastrErrorOptions());
                return false;
            }

            this.$message.set('');
        } else {
            this.$attachments.set([]);
        }

        this.configSchemaSection = {
            contentType: this.$contentType(),
            documentClass: this.$documentClass(),
            accessLevel: this.$accessLevel(),
            attachments: this.$attachments(),
        } as ConfigSchemaSectionExtraContent;

        let div = document.createElement('div');
        div.innerHTML = this.$message()?.replaceAll('<br />', '\n');
        this.configSchemaSection.message = div.textContent || div.innerText || '';

        div = document.createElement('div');
        div.innerHTML = this.$waitingMessage()?.replaceAll('<br />', '\n');
        this.configSchemaSection.waitingMessage = div.textContent || div.innerText || '';

        Utils.updSignal(this.$model, x => {
            let configSchema = JSON.parse(this.$model().configSchema) as ConfigSchema;

            switch (this.$model().typeId) {
                case FlowObjectType.FinishContent:
                    configSchema.taskFinishContent.extraContent = this.configSchemaSection;
                    x.configSchema = JSON.stringify(configSchema);
                    break;

                case FlowObjectType.GatewayApprove:
                    configSchema.taskApprove.extraContent = this.configSchemaSection;
                    x.configSchema = JSON.stringify(configSchema);
                    break;

                case FlowObjectType.TaskAcknowledge:
                    configSchema.taskAcknowledge.extraContent = this.configSchemaSection;
                    x.configSchema = JSON.stringify(configSchema);
                    break;
            }

            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    closeForm() {
        this._closeEvent.emit();
    }

    // #region [===== private methods =====]
    // #endregion

    private initFromModel(skipInitialSetup?: boolean) {
        if (!skipInitialSetup) {
            let configSchema = JSON.parse(this.$model().configSchema) as ConfigSchema;

            switch (this.$model().typeId) {
                case FlowObjectType.FinishContent:
                    this.configSchemaSection = configSchema.taskFinishContent.extraContent;
                    break;

                case FlowObjectType.GatewayApprove:
                    this.configSchemaSection = configSchema.taskApprove.extraContent;
                    break;

                case FlowObjectType.TaskAcknowledge:
                    this.configSchemaSection = configSchema.taskAcknowledge.extraContent;
                    break;
            }
        }

        this.$message.set(this.configSchemaSection.message?.replaceAll('\n', '<br />'));
        this.$waitingMessage.set(this.configSchemaSection.waitingMessage?.replaceAll('\n', '<br />'));

        if (this.configSchemaSection.contentType != null) {
            this.$contentType.set(this.configSchemaSection.contentType);
        } else {
            this.$contentType.set(this.$shouldShowEnableExtraContent()
                ? ExtraContentType.Attachment
                : ExtraContentType.Text
            );
        }

        Utils.setSignal(this.$documentClass, this.configSchemaSection.documentClass || new ConfigSchemaSectionDocumentClass());
        Utils.setSignal(this.$accessLevel, this.configSchemaSection.accessLevel || new ConfigSchemaSectionAccessLevel());
        Utils.setSignal(this.$attachments, this.configSchemaSection.attachments?.length > 0
            ? this.configSchemaSection.attachments.toSorted((a, b) => a.timestamp.localeCompare(b.timestamp))
            : []
        );

        if (this.configSchemaSection.attachments?.length > 0) {
            this.$enableExtraContent.set(true);
        }
    }
}
